import { PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMeterKeys } from "@/api/districts/queries";
import { CreateKeyDialog } from "@/components/settings/create-key-dialog";
import { CopyableCell } from "@/components/shared/copyable-cell";
import { DataTablePagination } from "@/components/table/data-table-pagination";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDistrict } from "@/contexts/district-context";

const DEFAULT_PAGE_SIZE = 20;

export function MeterKeysTab() {
	const { t, i18n } = useTranslation();
	const locale = i18n.language === "en" ? "en-GB" : "pl-PL";
	const { districtId } = useDistrict();
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const { data, isLoading } = useMeterKeys({
		page,
		page_size: pageSize,
		district: districtId,
	});
	const [dialogOpen, setDialogOpen] = useState(false);

	const keys = data?.results ?? [];

	function handlePageSizeChange(size: number) {
		setPageSize(size);
		setPage(1);
	}

	return (
		<>
			<CreateKeyDialog open={dialogOpen} onOpenChange={setDialogOpen} />
			<div className="flex min-h-0 flex-1 flex-col gap-3">
				<div className="flex items-start justify-between gap-4">
					<p className="max-w-[60%] text-xs text-muted-foreground">
						{t("meterKeys.description")}
					</p>
					<Button onClick={() => setDialogOpen(true)}>
						<PlusIcon className="size-4" />
						{t("meterKeys.createButton")}
					</Button>
				</div>

				<div className="relative min-h-0 flex-1">
					<div className="h-full min-h-0 overflow-auto rounded-md border">
						<Table>
							<TableHeader className="sticky top-0 z-10">
								<TableRow>
									<TableHead>{t("meterKeys.colKey")}</TableHead>
									<TableHead>{t("meterKeys.colApartment")}</TableHead>
									<TableHead>{t("meterKeys.colDistrict")}</TableHead>
									<TableHead>{t("meterKeys.colCreatedAt")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{isLoading ? (
									Array.from({ length: 5 }).map((_, i) => (
										<TableRow key={i}>
											{Array.from({ length: 4 }).map((_, j) => (
												<TableCell key={j}>
													<Skeleton className="h-4 w-full" />
												</TableCell>
											))}
										</TableRow>
									))
								) : !keys.length ? (
									<TableRow>
										<TableCell
											colSpan={4}
											className="py-8 text-center text-sm text-muted-foreground"
										>
											{t("meterKeys.empty")}
										</TableCell>
									</TableRow>
								) : (
									keys.map((k) => (
										<TableRow key={k.key}>
											<TableCell>
												<CopyableCell
													value={k.key}
													label={t("meterKeys.colKey")}
												/>
											</TableCell>
											<TableCell>
												{k.apartment_detail
													? `${k.apartment_detail.street} ${k.apartment_detail.apartment_number}`
													: k.apartment}
											</TableCell>
											<TableCell>{k.district_name}</TableCell>
											<TableCell className="text-muted-foreground">
												{new Date(k.created_at).toLocaleDateString(locale)}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</div>
				</div>

				{data && data.count > 0 && (
					<DataTablePagination
						page={page}
						pageSize={pageSize}
						count={data.count}
						onPageChange={setPage}
						onPageSizeChange={handlePageSizeChange}
						hasPrevious={data.previous !== null}
						hasNext={data.next !== null}
					/>
				)}
			</div>
		</>
	);
}
